import { Fragment, useRef, useState } from "react"
import type { ChangeEvent, Dispatch, DragEvent, FC, ReactElement, RefObject, SetStateAction } from "react"

import { AnimatePresence, motion } from "framer-motion"
import eq from "lodash-es/eq"
import includes from "lodash-es/includes"
import isEmpty from "lodash-es/isEmpty"
import isString from "lodash-es/isString"
import join from "lodash-es/join"
import Cropper, { type Area } from "react-easy-crop"
import { Trans } from "react-i18next"
import { useBoolean, useUpdate, useUpdateEffect } from "react-use"

import { toBase64 } from "$/utils/functions"

import { ErrorHint } from "@/3514/components/shared/error-hint/error-hint.component"
import { SpecialChars } from "@/3514/components/shared/typography/special-char/special-char.config"
import { ESpecialChar } from "@/3514/components/shared/typography/special-char/special-char.types"
import { Typography } from "@/3514/components/shared/typography/typography.component"
import {
    ETypographyColor,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight
} from "@/3514/components/shared/typography/typography.types"
import { buildCssClassName, withPressHandler } from "@/3514/utils"
import { type IUseTranslation, type TTranslationKeys, useTranslation } from "@/hooks"
import type { TEmptyCallback } from "@/shared/types/functions"

import { imageUploadAssets as assets } from "./image-upload.assets"
import {
    type TImageUploadPhotoQuery as TQuery,
    type TImageUploadPhoto as TUploadPhoto,
    useImageUploadPhotoQuery as usePhotoQuery
} from "./image-upload.query"
import { imageUploadStyles as classes } from "./image-upload.styles"
import { imageUploadUtils as utils } from "./image-upload.utils"

type _TProps = {
    onChange(params: { originalBase64: string | ArrayBuffer; croppedBase64: string | ArrayBuffer }): Promise<void>
    isDisabled?: boolean
    acceptedFormats?: string[]
    maxFileSizeMB?: number
    minZoom?: number
    maxZoom?: number
    initialZoom?: number
    zoomStep?: number
    initialPhoto?: string | null
}

type _TError = "size" | "type" | "general"

const errorTextsMap: { [key in _TError]: keyof TTranslationKeys } = {
    size: "common.imageUpload.sizeError",
    type: "common.imageUpload.formatError",
    general: "common.imageUpload.generalError"
}

const MAX_CROPPED_IMAGE_SIZE = 1024

const { isUrlValue, isDataUrl, isBase64Value } = utils

const ImageUpload: FC<_TProps> = ({
    onChange,
    isDisabled = false,
    acceptedFormats = ["image/png", "image/jpeg", "image/jpg"],
    maxFileSizeMB = 5,
    minZoom = 1,
    maxZoom = 3,
    initialZoom = 1.3,
    zoomStep = 0.1,
    initialPhoto = null
}: _TProps): ReactElement => {
    const [error, setError]: [_TError, Dispatch<SetStateAction<_TError>>] = useState<_TError>(null)

    const [isDragging, setIsDragging]: ReturnType<typeof useBoolean> = useBoolean(false)

    const [isLoaded, setIsLoaded]: ReturnType<typeof useBoolean> = useBoolean(false)

    const [zoom, setZoom]: [number, Dispatch<SetStateAction<number>>] = useState<number>(initialZoom)

    const [crop, setCrop]: [{ x: number; y: number }, Dispatch<SetStateAction<{ x: number; y: number }>>] = useState<{
        x: number
        y: number
    }>({ x: 0, y: 0 })

    const forceUpdate: TEmptyCallback = useUpdate()

    const originalBase64Ref: RefObject<string | ArrayBuffer> = useRef<string | ArrayBuffer>(String(""))
    const originalBase64ValueRef: RefObject<string | ArrayBuffer> = useRef<string | ArrayBuffer>(String(""))

    const dragCounterRef: RefObject<number> = useRef<number>(0)

    const inputRef: RefObject<HTMLInputElement> = useRef<HTMLInputElement>(null)

    const { t }: IUseTranslation = useTranslation()

    const { data: resolvedPhoto, isError: isPhotoError }: TQuery = usePhotoQuery({
        enabled: !isEmpty(initialPhoto) && isString(initialPhoto) && isUrlValue(initialPhoto),
        photo: initialPhoto,
        initialData: ((): TUploadPhoto | undefined => {
            if (isDataUrl(initialPhoto))
                return {
                    dataUrl: initialPhoto,
                    base64Value: initialPhoto.replace(/^.*,/, "")
                }

            if (isBase64Value(initialPhoto))
                return {
                    dataUrl: `data:image/png;base64,${initialPhoto}`,
                    base64Value: initialPhoto
                }

            return undefined
        })()
    })

    useUpdateEffect((): void => {
        if (resolvedPhoto && !isLoaded) {
            originalBase64Ref.current = resolvedPhoto.dataUrl
            originalBase64ValueRef.current = resolvedPhoto.base64Value
            setIsLoaded(true)
        }
    }, [resolvedPhoto, isLoaded])

    useUpdateEffect((): void => {
        if (isPhotoError && !error) setError("general")
    }, [isPhotoError, error])

    function handleDrag(e: DragEvent<HTMLDivElement>): void {
        e.preventDefault()
        e.stopPropagation()

        if (isDisabled) return

        if (eq(e.type, "dragenter")) {
            if (dragCounterRef.current === 0 && isLoaded) {
                setIsLoaded(false)
                originalBase64Ref.current = String("")
            }

            dragCounterRef.current += 1
            setIsDragging(true)
        } else if (eq(e.type, "dragleave")) {
            dragCounterRef.current -= 1
            if (dragCounterRef.current === 0) {
                setIsDragging(false)
            }
        }
    }

    function validateFile(file: File): boolean {
        setError(null)

        if (!includes<string>(acceptedFormats, file.type)) {
            setError("type")
            return false
        }

        const maxSizeBytes: number = maxFileSizeMB * 1024 * 1024

        if (file.size > maxSizeBytes) {
            setError("size")
            return false
        }

        return true
    }

    async function processFile(file: File): Promise<void> {
        if (!validateFile(file)) return

        setIsLoaded(false)
        setIsDragging(false)

        originalBase64Ref.current = String("")
        originalBase64ValueRef.current = String("")
        dragCounterRef.current = 0

        try {
            const _originalBase64: string | ArrayBuffer = await toBase64(file)

            originalBase64Ref.current = `data:${file.type};base64,${_originalBase64}`
            originalBase64ValueRef.current = _originalBase64 as string

            forceUpdate()
            setIsLoaded(true)

            setError(null)
        } catch {
            setError("general")
        }
    }

    async function generateCroppedImage(croppedAreaPixels: Area): Promise<void> {
        if (!croppedAreaPixels) return

        const image = new Image()
        image.src = originalBase64Ref.current as string

        image.onload = async (): Promise<void> => {
            const size: number = Math.max(croppedAreaPixels.width, croppedAreaPixels.height)
            const outputSize: number = Math.max(1, Math.round(Math.min(size, MAX_CROPPED_IMAGE_SIZE)))
            const scaledWidth: number = (croppedAreaPixels.width / size) * outputSize
            const scaledHeight: number = (croppedAreaPixels.height / size) * outputSize
            const offsetX: number = (outputSize - scaledWidth) / 2
            const offsetY: number = (outputSize - scaledHeight) / 2

            const canvas: HTMLCanvasElement = document.createElement("canvas")
            canvas.width = outputSize
            canvas.height = outputSize

            const ctx: CanvasRenderingContext2D = canvas.getContext("2d", { alpha: true })

            if (!ctx) return

            ctx.clearRect(0, 0, canvas.width, canvas.height)

            ctx.drawImage(
                image,
                croppedAreaPixels.x,
                croppedAreaPixels.y,
                croppedAreaPixels.width,
                croppedAreaPixels.height,
                offsetX,
                offsetY,
                scaledWidth,
                scaledHeight
            )

            const radius: number = outputSize / 2
            const centerX: number = outputSize / 2
            const centerY: number = outputSize / 2

            const imageData: ImageData = ctx.getImageData(0, 0, outputSize, outputSize)

            const { data } = imageData

            for (let i: number = 0; i < data.length; i += 4) {
                const pixelIndex: number = i / 4
                const pixelX: number = pixelIndex % outputSize
                const pixelY: number = Math.floor(pixelIndex / outputSize)

                const distanceFromCenter: number = Math.sqrt((pixelX - centerX) ** 2 + (pixelY - centerY) ** 2)

                if (distanceFromCenter > radius) data[i + 3] = 0
            }

            ctx.putImageData(imageData, 0, 0)

            try {
                await onChange({
                    originalBase64: originalBase64ValueRef.current,
                    croppedBase64: `data:image/webp;base64,${canvas.toDataURL("image/webp", 0.9).replace(/^.*,/, "")}`
                })
            } catch {
                setError("general")
            }
        }
    }

    function handleDrop(e: DragEvent<HTMLDivElement>): void {
        e.preventDefault()
        e.stopPropagation()

        setIsDragging(false)

        dragCounterRef.current = 0

        if (isDisabled) return

        const { files }: DataTransfer = e.dataTransfer

        if (files && files.length > 0) void processFile(files[0])
    }

    function handleInputChange(e: ChangeEvent<HTMLInputElement>): void {
        const { files }: HTMLInputElement = e.target

        if (files && files.length > 0) void processFile(files[0])
    }

    function handleManualBrowsingAction(): void {
        if (!isDisabled && inputRef.current) inputRef.current.click()
    }

    function handleZoomIn(): void {
        setZoom((prev: number): number => Math.min(prev + 0.1, 3))
    }

    function handleZoomOut(): void {
        setZoom((prev: number): number => Math.max(prev - 0.1, 1))
    }

    function handleSliderChange(e: ChangeEvent<HTMLInputElement>): void {
        setZoom(Number(e.target.value))
    }

    return (
        <div
            className={classes.container({ isDragging, isLoaded, isError: !!error })}
            onDragEnterCapture={handleDrag}
            onDragLeaveCapture={handleDrag}
            onDragOverCapture={handleDrag}
            onDropCapture={handleDrop}
        >
            <input
                type="file"
                ref={inputRef}
                onChange={handleInputChange}
                accept={join(acceptedFormats, ",")}
                disabled={isDisabled}
                aria-disabled={isDisabled}
                className={classes.input()}
                aria-label="Upload image file"
            />

            <div className={buildCssClassName(classes.dropZone())}>
                <div className={classes.content()}>
                    <AnimatePresence mode="wait">
                        {isLoaded ? (
                            <motion.div
                                key="fu-loaded"
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.8 }}
                                transition={{ duration: 0.2 }}
                            >
                                <div className={classes.cropperContainer()}>
                                    <Cropper
                                        image={originalBase64Ref.current as string}
                                        objectFit="contain"
                                        cropShape="round"
                                        aspect={1}
                                        maxZoom={3}
                                        minZoom={1}
                                        zoomSpeed={1}
                                        showGrid={false}
                                        zoomWithScroll={false}
                                        restrictPosition={false}
                                        crop={crop}
                                        zoom={zoom}
                                        onCropChange={setCrop}
                                        onZoomChange={setZoom}
                                        classes={{
                                            cropAreaClassName: classes.cropperArea()
                                        }}
                                        onCropComplete={(_croppedArea: Area, croppedAreaPixels: Area): Promise<void> =>
                                            generateCroppedImage(croppedAreaPixels)
                                        }
                                    />
                                </div>
                            </motion.div>
                        ) : (
                            <Fragment>
                                {isDragging ? (
                                    <motion.div
                                        key="fu-drag"
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.8 }}
                                        transition={{ duration: 0.2 }}
                                        className={classes.dragContainer()}
                                    >
                                        <assets.ImageUpload />
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="fu-default"
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.8 }}
                                        transition={{ duration: 0.2 }}
                                        style={{ rowGap: "10px", display: "flex", flexDirection: "column" }}
                                    >
                                        <Fragment>
                                            <div className={classes.icon()}>
                                                <assets.ImagePlaceholder />
                                            </div>

                                            <Trans
                                                parent="p"
                                                i18nKey="participantSide.onboarding.profile.imageUploadLabel"
                                                className={classes.actionText()}
                                                components={{
                                                    strong: (
                                                        <button
                                                            type="button"
                                                            tabIndex={isDisabled ? -1 : 0}
                                                            aria-label={t(
                                                                "participantSide.onboarding.profile.imageUploadLabel"
                                                            )}
                                                            aria-disabled={isDisabled}
                                                            className={classes.actionTextStrong()}
                                                            onClick={withPressHandler(handleManualBrowsingAction)}
                                                            onKeyDown={withPressHandler(handleManualBrowsingAction)}
                                                        />
                                                    )
                                                }}
                                            />
                                        </Fragment>
                                    </motion.div>
                                )}
                            </Fragment>
                        )}
                    </AnimatePresence>

                    {isLoaded ? (
                        <div className={classes.zoomControls()}>
                            <div
                                aria-label="Interact to make zoom smaller"
                                role="button"
                                tabIndex={-1}
                                className={classes.zoomControl()}
                                onKeyDown={withPressHandler(handleZoomOut)}
                                onClick={withPressHandler(handleZoomOut)}
                            >
                                <Typography
                                    text={SpecialChars[ESpecialChar.Minus]}
                                    className={classes.zoomButton()}
                                    size={ETypographySize.Small}
                                    weight={ETypographyWeight.Bold}
                                    color={ETypographyColor.Blue300}
                                    tag={ETypographyTag.Span}
                                />
                            </div>
                            <input
                                min={minZoom}
                                max={maxZoom}
                                step={zoomStep}
                                value={zoom}
                                onChange={handleSliderChange}
                                className={classes.zoomSlider()}
                                type="range"
                                aria-label="Pull the slider to change image zoom"
                            />
                            <div
                                aria-label="Interact to make zoom bigger"
                                role="button"
                                tabIndex={-1}
                                className={classes.zoomControl()}
                                onKeyDown={withPressHandler(handleZoomIn)}
                                onClick={withPressHandler(handleZoomIn)}
                            >
                                <Typography
                                    text={SpecialChars[ESpecialChar.Plus]}
                                    className={classes.zoomButton()}
                                    size={ETypographySize.Small}
                                    weight={ETypographyWeight.Bold}
                                    color={ETypographyColor.Blue300}
                                    tag={ETypographyTag.Span}
                                />
                            </div>
                        </div>
                    ) : (
                        <Typography
                            text={t("participantSide.onboarding.profile.imageUploadRule")}
                            size={ETypographySize.Tiny}
                            color={ETypographyColor.Gray23}
                        />
                    )}
                </div>
            </div>

            <ErrorHint isVisible={!!error} variant="error-secondary" role="alert" text={t(errorTextsMap[error])} />
        </div>
    )
}

ImageUpload.displayName = "ImageUpload"

export { ImageUpload }
